'use client';

import type { UiBlock } from '@trakd/ai';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { IconType } from 'react-icons';
import { PiPaperPlaneTiltBold } from 'react-icons/pi';
import {
  TbBolt,
  TbBrandMeta,
  TbBug,
  TbCloudNetwork,
  TbFileTextSpark,
  TbLifebuoy,
  TbShoppingBag,
  TbStars,
  TbWebhook,
  TbX,
} from 'react-icons/tb';

import { getIntegrationBlock, getReportBlock } from './chat-blocks/actions';
import { FeedbackBlock } from './chat-blocks/feedback-block';
import { HandoffBlock } from './chat-blocks/handoff-block';
import type { HandoffBlockPayload } from './chat-blocks/handoff-block';
import { IntegrationBlock } from './chat-blocks/integration-block';
import { ReportBlock } from './chat-blocks/report-block';
import { TicketBlock } from './chat-blocks/ticket-block';
import type {
  IntegrationBlockPayload,
  ReportBlockPayload,
  TicketBlockPayload,
} from './chat-blocks/types';
import { TrakdChatMessageRow } from './trakd-chat-message-row';

import { streamChatResponse } from '@/lib/chat/stream-client';
import { getOrCreateVisitorId } from '@/lib/chat/visitor-id';
import { createSupabaseBrowserClient } from '@/lib/supabase/browser';

interface PanelMessage {
  role: string;
  content: string;
  createdAt: string;
  block?: UiBlock;
  authorName?: string;
}

type AgentRole = 'vendas' | 'onboarding' | 'suporte';

export interface TrakdChatPanelProps {
  initialMessages: PanelMessage[];
  userName: string | null;
  onClose: () => void;
  variant?: 'app' | 'public';
  /** Texto inicial do input (ex.: aberto via "Falar com vendas" na /precos). */
  initialDraft?: string;
}

/** Default agente quando ainda não houve turn (deriva do pathname). */
function inferAgentFromPath(pathname: string | null): AgentRole {
  if (pathname?.startsWith('/onboarding/')) return 'onboarding';
  return 'suporte';
}

interface QuickAction {
  icon: IconType;
  label: string;
  prompt: string;
  /** Tom emocional do ícone — cor inline aplicada só no ícone */
  tone: string;
  kind: 'problem' | 'integration' | 'report' | 'feedback' | 'question';
  provider?: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    icon: TbBug,
    label: 'Relatar problema',
    prompt: 'Quero relatar um problema técnico que estou enfrentando. Vou descrever:',
    tone: '#FF6B6B',
    kind: 'problem',
  },
  {
    icon: TbShoppingBag,
    label: 'Integrar Hotmart',
    prompt: 'Como conecto a Hotmart no Trakd?',
    tone: '#FFB454',
    kind: 'integration',
    provider: 'hotmart',
  },
  {
    icon: TbFileTextSpark,
    label: 'Emitir relatório',
    prompt: 'Me mostra o último relatório.',
    tone: '#9061FF',
    kind: 'report',
  },
  {
    icon: TbStars,
    label: 'Enviar feedback',
    prompt: 'Quero dar um feedback.',
    tone: '#4AE3B5',
    kind: 'feedback',
  },
];

const PUBLIC_QUICK_ACTIONS: QuickAction[] = [
  {
    icon: TbWebhook,
    label: 'Integrações suportadas',
    prompt: 'Quais integrações o Trakd suporta?',
    tone: '#7C5CFF',
    kind: 'question',
  },
  {
    icon: TbBolt,
    label: 'Como funciona',
    prompt: 'Como o Trakd funciona?',
    tone: '#FFB454',
    kind: 'question',
  },
  {
    icon: TbCloudNetwork,
    label: 'Como instalar',
    prompt: 'Como instalo o Trakd no meu site?',
    tone: '#4AE3B5',
    kind: 'question',
  },
  {
    icon: TbBrandMeta,
    label: 'Meta pode bloquear?',
    prompt: 'A Meta pode bloquear minha conta por usar tracking avançado?',
    tone: '#4D8DFF',
    kind: 'question',
  },
];

function firstName(full: string | null): string {
  if (!full) return '';
  return full.trim().split(/\s+/)[0] ?? '';
}

/** Mapeia mensagens brutas da API para PanelMessage. Reutilizado no load inicial e no refetch de reconexão. */
function mapConvMessages(
  msgs: { role: string; content: string; created_at?: string; author_name?: string }[],
): PanelMessage[] {
  return msgs
    .map((msg): PanelMessage | null => {
      if (msg.role === 'user') {
        return {
          role: 'user',
          content: msg.content,
          createdAt: msg.created_at ?? new Date().toISOString(),
        };
      }
      if (msg.role === 'assistant') {
        return {
          role: 'assistant',
          content: msg.content,
          createdAt: msg.created_at ?? new Date().toISOString(),
        };
      }
      if (msg.role === 'human_agent') {
        return {
          role: 'assistant',
          content: msg.content,
          createdAt: msg.created_at ?? new Date().toISOString(),
          authorName: msg.author_name,
        };
      }
      return null;
    })
    .filter((m): m is PanelMessage => m !== null);
}

function renderBlock(block: UiBlock): React.ReactElement | null {
  switch (block.kind) {
    case 'feedback':
      return <FeedbackBlock />;
    case 'report':
      return <ReportBlock payload={block.payload as unknown as ReportBlockPayload} />;
    case 'integration':
      return <IntegrationBlock payload={block.payload as unknown as IntegrationBlockPayload} />;
    case 'ticket':
      return <TicketBlock payload={block.payload as unknown as TicketBlockPayload} />;
    case 'handoff':
      return <HandoffBlock payload={block.payload as unknown as HandoffBlockPayload} />;
    default:
      return null;
  }
}

export function TrakdChatPanel({
  initialMessages,
  userName,
  onClose,
  variant = 'app',
  initialDraft,
}: TrakdChatPanelProps): ReactElement {
  const pathname = usePathname();
  const [messages, setMessages] = useState<PanelMessage[]>(initialMessages);
  const [draft, setDraft] = useState(initialDraft ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  // Fluxo "Relatar problema": ativa o link de fallback pra abrir chamado no suporte.
  const [problemMode, setProblemMode] = useState(false);
  // Agente ativo: rastreado pra side-effects (transferências) mas não exibido na UI.
  const [, setActiveAgent] = useState<AgentRole>(() => inferAgentFromPath(pathname));
  // Estado do modo humano (quando a IA transfere para o time comercial).
  const [humanState, setHumanState] = useState<{
    conversationId: string;
    status: 'waiting' | 'active';
  } | null>(null);
  const visitorIdRef = useRef<string | null>(null);
  const stateLoadedRef = useRef(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  // Ref sincronizado com humanState pra leitura pura dentro de handlers/updaters.
  const humanStatusRef = useRef<'waiting' | 'active' | null>(null);
  // Controla se ja houve ao menos uma conexão SUBSCRIBED no canal; usado no refetch de reconexão.
  const hadConnectedRef = useRef(false);

  const isPublic = variant === 'public';

  // Mantém humanStatusRef sincronizado com humanState pra leitura pura nos handlers.
  useEffect(() => {
    humanStatusRef.current = humanState?.status ?? null;
  }, [humanState]);

  // Recalcula agente default quando muda de rota (até primeiro turn)
  useEffect(() => {
    setActiveAgent(inferAgentFromPath(pathname));
  }, [pathname]);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, isStreaming, streamingText]);

  // Mount effect: inicializa o visitorId da sessão de página. Decisão de
  // produto: refresh ZERA a conversa (sem restauração de estado no load); o
  // GET de estado segue em uso só no refetch de reconexão do canal realtime.
  useEffect(() => {
    if (!isPublic) return;
    if (stateLoadedRef.current) return;
    stateLoadedRef.current = true;
    visitorIdRef.current = getOrCreateVisitorId();
  }, [isPublic]);

  const activeConversationId = humanState?.conversationId ?? null;

  // Realtime: assina o canal sales:{conversationId} quando em modo humano.
  useEffect(() => {
    if (!activeConversationId) return;

    const supabase = createSupabaseBrowserClient();
    const channel = supabase.channel(`sales:${activeConversationId}`);

    channel
      .on('broadcast', { event: 'human_message' }, (payload: unknown) => {
        const data = (
          payload as { payload?: { content?: string; author_name?: string; created_at?: string } }
        ).payload;
        if (!data?.content) return;
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.content ?? '',
            authorName: data.author_name,
            createdAt: data.created_at ?? new Date().toISOString(),
          },
        ]);
      })
      .on('broadcast', { event: 'status' }, (payload: unknown) => {
        const data = (payload as { payload?: { human_status?: string; claimed_by_name?: string } })
          .payload;
        if (!data) return;
        const newStatus = data.human_status;
        if (newStatus === 'active') {
          // Updater puro: lê humanStatusRef fora do updater de state pra decidir
          // se é transição waiting->active; as duas chamadas de estado são separadas e
          // idempotentes — o guard evita duplicação mesmo em StrictMode (double-invoke).
          if (humanStatusRef.current === 'active') return;
          humanStatusRef.current = 'active';
          setHumanState((prev) => (prev ? { ...prev, status: 'active' as const } : prev));
          const handoffPayload: HandoffBlockPayload = {
            status: 'active',
            agent_name: data.claimed_by_name ?? undefined,
            conversation_id: activeConversationId,
          };
          setMessages((msgs) => [
            ...msgs,
            {
              role: 'assistant',
              content: '',
              block: {
                kind: 'handoff',
                payload: handoffPayload as unknown as Record<string, unknown>,
              },
              createdAt: new Date().toISOString(),
            },
          ]);
        } else if (newStatus === 'none' || newStatus === 'closed') {
          humanStatusRef.current = null;
          setHumanState(null);
        }
      })
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        if (!hadConnectedRef.current) {
          // Primeira conexão: apenas marca o ref.
          hadConnectedRef.current = true;
          return;
        }
        // Reconexão após queda: refaz o fetch do estado pra recuperar mensagens perdidas.
        const vid = visitorIdRef.current;
        if (!vid) return;
        void (async () => {
          try {
            const res = await fetch(`/api/chat/public/state?visitor=${encodeURIComponent(vid)}`);
            if (!res.ok) return;
            const resData = (await res.json()) as {
              conversation: null | {
                id: string;
                humanStatus: 'none' | 'waiting' | 'active' | 'closed';
                claimedByName?: string | null;
                messages: {
                  role: string;
                  content: string;
                  created_at?: string;
                  author_name?: string;
                }[];
              };
            };
            const conv = resData.conversation;
            if (!conv) return;
            const mapped = mapConvMessages(conv.messages);
            if (mapped.length > 0) setMessages(mapped);
            if (conv.humanStatus === 'waiting' || conv.humanStatus === 'active') {
              humanStatusRef.current = conv.humanStatus;
              setHumanState({ conversationId: conv.id, status: conv.humanStatus });
            } else {
              humanStatusRef.current = null;
              setHumanState(null);
            }
          } catch {
            // silencioso: falha de refetch não impede uso do chat
          }
        })();
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [activeConversationId]);

  function pushBlock(block: UiBlock): void {
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: '', block, createdAt: new Date().toISOString() },
    ]);
  }

  // Bloco vindo da IA (tool open_integration) chega sem os passos do catálogo.
  // Enriquece com getIntegrationBlock pra ficar igual ao do chip. Fallback no raw.
  async function pushBlockEnriched(block: UiBlock): Promise<void> {
    if (block.kind === 'integration') {
      const provider =
        typeof block.payload['provider'] === 'string' ? (block.payload['provider'] as string) : '';
      const hasSteps = Array.isArray((block.payload as { steps?: unknown }).steps);
      if (provider && !hasSteps) {
        try {
          const enriched = await getIntegrationBlock(provider);
          pushBlock({
            kind: 'integration',
            payload: enriched as unknown as Record<string, unknown>,
          });
          return;
        } catch {
          // cai no push do bloco cru abaixo
        }
      }
    }

    if (block.kind === 'handoff') {
      const hp = block.payload as unknown as HandoffBlockPayload;
      pushBlock(block);
      if (hp.conversation_id) {
        setHumanState({ conversationId: hp.conversation_id, status: hp.status ?? 'waiting' });
      } else {
        // Sem conversation_id: tenta buscar do estado remoto
        const vid = visitorIdRef.current;
        if (vid) {
          void (async () => {
            try {
              const res = await fetch(`/api/chat/public/state?visitor=${encodeURIComponent(vid)}`);
              if (!res.ok) return;
              const data = (await res.json()) as {
                conversation: null | {
                  id: string;
                  humanStatus: 'none' | 'waiting' | 'active' | 'closed';
                };
              };
              if (data.conversation?.id) {
                setHumanState({
                  conversationId: data.conversation.id,
                  status: (data.conversation.humanStatus === 'active' ? 'active' : 'waiting') as
                    | 'waiting'
                    | 'active',
                });
              }
            } catch {
              // silencioso
            }
          })();
        }
      }
      return;
    }

    pushBlock(block);
  }

  function onChip(qa: QuickAction): void {
    if (qa.kind === 'question') {
      dispatch(qa.prompt);
      return;
    }
    if (qa.kind === 'feedback') {
      pushBlock({ kind: 'feedback', payload: {} });
      return;
    }
    if (qa.kind === 'report') {
      void getReportBlock().then((payload) =>
        pushBlock({ kind: 'report', payload: payload as unknown as Record<string, unknown> }),
      );
      return;
    }
    if (qa.kind === 'integration') {
      void getIntegrationBlock(qa.provider ?? 'hotmart').then((payload) =>
        pushBlock({ kind: 'integration', payload: payload as unknown as Record<string, unknown> }),
      );
      return;
    }
    if (qa.kind === 'problem') setProblemMode(true);
    dispatch(qa.prompt);
  }

  function dispatch(text: string): void {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: trimmed, createdAt: new Date().toISOString() },
    ]);
    setDraft('');
    setError(null);
    setStreamingText('');
    setIsStreaming(true);

    void (async () => {
      let assembled = '';
      try {
        for await (const chunk of streamChatResponse({
          message: trimmed,
          ...(pathname ? { pathname } : {}),
          ...(isPublic ? { endpoint: '/api/chat/public' } : {}),
          ...(isPublic && visitorIdRef.current ? { visitorId: visitorIdRef.current } : {}),
        })) {
          if (chunk.type === 'content') {
            assembled += chunk.content;
            setStreamingText(assembled);
          } else if (chunk.type === 'done') {
            if (chunk.meta?.agentRole) setActiveAgent(chunk.meta.agentRole);
            if (assembled.trim()) {
              setMessages((prev) => [
                ...prev,
                {
                  role: 'assistant',
                  content: assembled,
                  createdAt: new Date().toISOString(),
                },
              ]);
            }
            setStreamingText('');
          } else if (chunk.type === 'error') {
            setError(chunk.error);
            setStreamingText('');
          } else if (chunk.type === 'ui_block' && chunk.block) {
            void pushBlockEnriched(chunk.block);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro inesperado.');
        setStreamingText('');
      } finally {
        setIsStreaming(false);
      }
    })();
  }

  const isEmpty = messages.length === 0 && !isStreaming;
  const actions = isPublic ? PUBLIC_QUICK_ACTIONS : QUICK_ACTIONS;
  const greeting = isPublic ? 'Olá' : firstName(userName) ? `Oi, ${firstName(userName)}` : 'Oi';
  const hasTicketBlock = messages.some((m) => m.block?.kind === 'ticket');
  // Fallback discreto: no fluxo de problema, se nenhum ticket foi aberto, oferece
  // abrir o chamado direto na página de suporte (não depende da IA chamar a tool).
  const showOpenTicketFallback = !isPublic && problemMode && !hasTicketBlock && !isStreaming;

  return (
    <div
      style={{
        width: 'min(420px, calc(100vw - 32px))',
        height: 'min(640px, calc(100vh - 120px))',
      }}
      className="border-violet/15 shadow-trakd-glow-strong bg-bg relative flex flex-col overflow-hidden rounded-3xl border"
    >
      {/* === BACKGROUND CINEMATICO === */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 50% at 50% 35%, rgba(144, 97, 255, 0.42) 0%, rgba(144, 97, 255, 0.18) 30%, transparent 65%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 -left-16 h-72 w-72 rounded-full blur-3xl"
        style={{ background: 'rgba(159, 17, 206, 0.25)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.92' numOctaves='2' stitchTiles='stitch'/></filter><rect width='220' height='220' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* === TOP BAR — avatar com status + X de fechar (fundo solido pra cobrir o glow) === */}
      <header
        className="border-border/40 relative z-20 flex items-center justify-between border-b px-5 py-4"
        style={{ background: 'oklch(0.16 0.013 270)' }}
      >
        <div className="flex items-center gap-2.5">
          <span className="relative">
            <span
              className="ring-violet/50 block h-9 w-9 overflow-hidden rounded-full bg-contain bg-center bg-no-repeat ring-2"
              style={{ backgroundImage: 'url(/icons/icon-192.png)' }}
              role="img"
              aria-label="Trakd"
            />
            <span
              aria-hidden
              className="bg-success ring-bg absolute right-[-1px] bottom-[-1px] h-2.5 w-2.5 rounded-full ring-2"
            />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-ink text-sm font-semibold tracking-tight">Trakd IA</span>
            <span className="text-ink-muted text-[10px] font-medium tracking-wide">Online</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="text-ink bg-surface-elevated hover:bg-violet/20 ring-border flex h-9 w-9 items-center justify-center rounded-full ring-1 transition hover:text-white"
        >
          <TbX size={18} strokeWidth={2.4} />
        </button>
      </header>

      {/* === BODY === */}
      {isEmpty ? (
        // ============ HERO CENTRADO (estado inicial — não mexer) ============
        <div className="scrollbar-soft relative z-10 flex flex-1 flex-col overflow-y-auto">
          <div className="flex flex-1 flex-col items-center justify-center gap-7 px-6 pb-8 text-center">
            <div className="space-y-3">
              <p className="text-ink text-[34px] leading-[1.05] font-semibold tracking-tighter">
                {greeting}{' '}
                <span aria-hidden className="inline-block translate-y-[-3px]">
                  👋
                </span>
              </p>
              <p className="text-ink text-[28px] leading-[1.1] font-semibold tracking-tighter">
                Como podemos te <span className="text-trakd-brand">ajudar</span> hoje?
              </p>
            </div>

            {/* Input grande central */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                dispatch(draft);
              }}
              className="w-full max-w-sm"
            >
              <div className="border-border-subtle/60 bg-surface-elevated/50 focus-within:border-violet/60 focus-within:bg-surface-elevated focus-within:shadow-trakd-glow-violet flex w-full items-center gap-2 rounded-2xl border px-4 py-3 text-left backdrop-blur-xl transition-all">
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Manda sua dúvida…"
                  aria-label="Mensagem para o assistente Trakd"
                  className="text-ink placeholder:text-ink-subtle flex-1 bg-transparent text-sm focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={!draft.trim()}
                  aria-label="Enviar"
                  className="bg-trakd-fill shadow-trakd-glow-subtle enabled:hover:shadow-trakd-glow-violet text-on-brand flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all disabled:opacity-30 disabled:shadow-none"
                >
                  <PiPaperPlaneTiltBold size={13} />
                </button>
              </div>
            </form>

            {/* Chips com ícones coloridos */}
            <div className="flex w-full max-w-sm flex-wrap justify-center gap-2">
              {actions.map((qa) => {
                const Icon = qa.icon;
                return (
                  <button
                    key={qa.label}
                    type="button"
                    onClick={() => onChip(qa)}
                    className="group bg-surface-elevated/60 hover:bg-surface-elevated hover:border-violet/50 border-border-subtle/60 text-ink inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium backdrop-blur-sm transition-all"
                  >
                    <Icon size={14} strokeWidth={1.9} style={{ color: qa.tone }} />
                    {qa.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        // ============ CONVERSAÇÃO — messages scrollam, form gruda no fundo ============
        <>
          <div
            ref={messagesRef}
            className="scrollbar-soft relative z-10 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-5"
          >
            {messages.map((m, i) =>
              m.block ? (
                <div key={i} className="max-w-[88%] self-start">
                  {renderBlock(m.block)}
                </div>
              ) : (
                <TrakdChatMessageRow
                  key={i}
                  role={m.role}
                  content={m.content}
                  authorLabel={m.authorName ? `${m.authorName} · Trakd` : undefined}
                />
              ),
            )}
            {isStreaming && streamingText.length > 0 && (
              <TrakdChatMessageRow role="assistant" content={streamingText} />
            )}
            {isStreaming && streamingText.length === 0 && !humanState && <TypingIndicator />}
            {error && (
              <p className="text-error rounded-xl bg-red-500/10 px-3 py-2 text-xs">{error}</p>
            )}
            {showOpenTicketFallback && (
              <a
                href="/suporte/meus-tickets?tab=abrir"
                className="border-border-subtle/60 bg-surface-elevated/40 text-ink-muted hover:text-ink hover:border-violet/40 inline-flex max-w-[88%] items-center gap-1.5 self-start rounded-full border px-3 py-1.5 text-[11px] transition-colors"
              >
                <TbLifebuoy size={13} className="text-violet shrink-0" strokeWidth={1.9} />
                Não resolveu? Abrir chamado no suporte
              </a>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              dispatch(draft);
            }}
            className="relative z-10 shrink-0 px-4 pt-2 pb-3"
          >
            <div className="border-border-subtle/60 bg-surface-elevated/60 focus-within:border-violet/60 focus-within:bg-surface-elevated focus-within:shadow-trakd-glow-subtle flex items-end gap-2 rounded-2xl border px-3 py-2 backdrop-blur-xl transition-all">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    dispatch(draft);
                  }
                }}
                placeholder="Pergunta qualquer coisa…"
                rows={1}
                maxLength={2000}
                aria-label="Mensagem para o assistente Trakd"
                className="text-ink placeholder:text-ink-subtle scrollbar-ghost max-h-32 min-h-[26px] flex-1 resize-none bg-transparent py-1.5 text-sm focus:outline-none"
              />
              <button
                type="submit"
                disabled={!draft.trim() || isStreaming}
                aria-label="Enviar"
                className="bg-trakd-fill shadow-trakd-glow-subtle enabled:hover:shadow-trakd-glow-violet text-on-brand flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all disabled:opacity-30 disabled:shadow-none"
              >
                <PiPaperPlaneTiltBold size={14} />
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 px-1">
      <span
        className="bg-violet h-1.5 w-1.5 rounded-full"
        style={{ animation: 'tk-typing 1.2s ease-in-out infinite' }}
      />
      <span
        className="bg-violet h-1.5 w-1.5 rounded-full"
        style={{ animation: 'tk-typing 1.2s ease-in-out 0.15s infinite' }}
      />
      <span
        className="bg-violet h-1.5 w-1.5 rounded-full"
        style={{ animation: 'tk-typing 1.2s ease-in-out 0.3s infinite' }}
      />
      <style>{`@keyframes tk-typing { 0%, 60%, 100% { opacity: 0.3; transform: translateY(0) } 30% { opacity: 1; transform: translateY(-3px) } }`}</style>
    </div>
  );
}
